import { useCallback, useState } from "react";
import {
  BillEntity,
  BillListStatus,
  GetAllBillsUseCase,
  PaginationInfo,
} from "../../domain";
import { BillRepositoryImpl } from "../../data/repositories/BillRepositoryImpl";

const billRepository = new BillRepositoryImpl();
const getAllBillsUseCase = new GetAllBillsUseCase(billRepository);

export interface BillListParams {
  page?: number;
  limit?: number;
  status?: BillListStatus;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: "created_at" | "updated_at" | "filename" | "status" | "original_filename";
  sortOrder?: "asc" | "desc";
}

export const useBillList = () => {
  const [bills, setBills] = useState<BillEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBills = useCallback(async (params: BillListParams = {}) => {
    setLoading(true);
    setError(null);

    const limit = params.limit ?? 10;
    const page = params.page ?? 1;

    try {
      const result = await getAllBillsUseCase.execute({
        limit,
        offset: Math.max(page - 1, 0) * limit,
        status: params.status ?? "all",
        search: params.search?.trim() || undefined,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        sort_by: params.sortBy ?? "created_at",
        sort_order: params.sortOrder ?? "desc",
      });

      setBills(result.bills);
      setPagination(result.pagination);
    } catch (error: any) {
      setError(error.message || "Failed to load bills");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    bills,
    pagination,
    loading,
    error,
    fetchBills,
    clearError,
  };
};
